<!-- eslint-disable vue/multi-word-component-names -- Existing component, kept for backward compatibility -->
<template>
    <Head title="Your People" />
    <BaseLayout gap="lg">
        <BaseNotification
            v-if="showImportBanner"
            theme="success"
            :text="`Successfully added ${imported_count} ${imported_count === 1 ? 'person' : 'people'} from your calendar`"
            @dismiss="dismissBanner" />
        <BaseNotification
            v-if="showCsvImportBanner"
            theme="success"
            :text="`Successfully imported ${csvImportCount} ${csvImportCount === 1 ? 'person' : 'people'} from CSV`"
            @dismiss="dismissCsvBanner" />
        <BasePageHeader title="Your People">
            <template v-if="showEmptyState" #subtitle>
                Adding people from your network can help Nadia remember key context about who you work with. This information is private and will stay between you and Nadia.
            </template>
            <template v-else #subtitle>
                <span>Nadia will track who is on your team and who you work with, to help you better collaborate.</span>
                <br />
                <span>She'll add people you mention automatically, but the more context you provide, the better her coaching will be.</span>
                <br />
                <span>
                    Like all her coaching, this information is fully private and visible only to you.
                    <a
                        href="https://support.valence.co/en/articles/11785623-introducing-people-in-nadia"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-flex items-center gap-1 text-[#0066CC] hover:text-[#0052A3] transition-colors duration-200 ml-1"
                        @click="handleHelpLinkClick">
                        Learn more
                        <i class="bi bi-box-arrow-up-right text-xs" aria-hidden="true"></i>
                        <span class="sr-only">(opens in a new tab)</span>
                    </a>
                </span>
                <div v-if="public_profile_enabled" class="mt-2">
                    <span class="inline-flex gap-2 scaffold-details-small">
                        <img :src="CollabFilledIcon" alt="" aria-hidden="true" class="h-4 w-auto" /> A connected icon means a person has a linked Collaboration Profile, so Nadia
                        has the context they chose to share about how they work best.
                    </span>
                </div>
            </template>
            <template v-if="showEmptyState" #actions>
                <AddPeopleMenu
                    :calendar-import-enabled="calendar_import_enabled"
                    :has-calendar-integration="has_calendar_integration"
                    :csv-import-enabled="csv_import_enabled"
                    @calendar="navigateToCalendarImport"
                    @connect-calendar="showCalendarSettings = true"
                    @csv="showCsvImportDialog = true"
                    @manual="createConnection" />
            </template>
        </BasePageHeader>
        <div v-if="!showEmptyState" class="flex flex-wrap gap-4 justify-between items-center mb-2">
            <div class="flex flex-wrap gap-2 self-start items-center">
                <!-- FIXME: Replace these filter buttons with an appropriate ToggleGroup component. -->
                <BaseButton
                    v-for="{ value, name } in filterToNameMap"
                    :key="value"
                    theme="neutral"
                    size="sm"
                    class="rounded-full whitespace-nowrap border-border text-toggle-text scaffold-body-small"
                    :class="filter === value ? 'bg-toggle-option-selected hover:bg-toggle-option-selected' : 'hover:bg-toggle-option-hover hover:border-border-strong'"
                    :aria-pressed="filter === value"
                    @click="handleChangeFilter(value)">
                    {{ name }}
                </BaseButton>
                <!-- Single meaningful facet: compact inline control -->
                <div v-if="showSingleFacetControl" class="inline-flex items-center gap-2 rounded-full border border-border-strong bg-surface-raised py-1 pl-3 pr-1">
                    <span v-if="singleFacet?.name" class="body-small-semibold uppercase tracking-[0.08em] text-ink-muted">{{ singleFacet.name }}</span>
                    <BaseSelect
                        :model-value="singleFacetValue"
                        :options="singleFacetOptions"
                        trigger-class="h-[28px]! min-w-36! border-0! bg-surface-sunken px-3! py-1.5! rounded-full!"
                        @update:model-value="handleSingleFacetChange" />
                </div>
                <!-- Multiple facets: modal trigger -->
                <FacetFilterModal v-if="meaningfulFacets.length > 1" v-model="activeFacetFilters" :facets="meaningfulFacets" @update:model-value="handleFacetFiltersChange" />
            </div>
            <!-- Anchored instance opened from a card's kebab "Merge" action. Mounted
                 unconditionally so its open-watcher (which loads candidates) is registered
                 before `open` flips to true. -->
            <MergeConnectionsDialog v-model:open="showKebabMergeDialog" :current-connection="mergeAnchorConnection ?? undefined" @merged="filterConnections" />
            <ConfirmationDialog
                v-model:open="showDeleteDialog"
                title="Delete person?"
                :text="`Are you sure you want to delete ${connectionPendingDelete?.persona?.name || 'this person'}? This action cannot be undone.`"
                confirm-label="Delete"
                @confirm="confirmDelete" />
            <AddPeopleMenu
                :calendar-import-enabled="calendar_import_enabled"
                :has-calendar-integration="has_calendar_integration"
                :csv-import-enabled="csv_import_enabled"
                @calendar="navigateToCalendarImport"
                @connect-calendar="showCalendarSettings = true"
                @csv="showCsvImportDialog = true"
                @manual="createConnection" />
        </div>
        <div v-if="!showEmptyState" class="md:-mt-6 flex flex-wrap gap-4 items-center">
            <div class="relative max-w-sm grow">
                <div class="absolute top-1/2 -translate-y-1/2 left-0 pl-3 pointer-events-none">
                    <i class="bi bi-search text-ink-muted" aria-hidden="true"></i>
                </div>
                <input
                    v-model="searchQuery"
                    type="text"
                    aria-label="Search by name or job title"
                    placeholder="Search by name or job title"
                    class="body-small focus-visible:outline-hidden rounded-lg p-2 pl-10 border border-control-border bg-control-bg w-full" />
            </div>
            <BaseSelect v-model="sortOption" :options="sortFieldOptions" trigger-class="h-[38px]! px-3! rounded-lg! w-48!" @update:model-value="filterConnections" />
        </div>
        <div v-if="hasActiveFacetFilters && !showEmptyState" class="md:-mt-2 flex flex-wrap items-center gap-2">
            <span class="body-small text-ink-muted">Active filters:</span>
            <BaseButton v-for="entry in activeFacetFilterEntries" :key="entry.id" theme="neutral" size="sm" class="gap-2 rounded-full px-3.5" @click="removeFacetFilter(entry.id)">
                <span class="body-small-semibold text-ink">{{ entry.name }}</span>
                <span class="mx-1 text-ink-muted">{{ entry.value }}</span>
                <i class="bi bi-x text-base/none" aria-hidden="true"></i>
            </BaseButton>
            <BaseButton theme="tertiary" size="sm" class="px-0 text-ink-muted hover:text-ink" @click="clearFacetFilters">Clear all</BaseButton>
        </div>

        <div class="connections-grid" aria-live="polite" aria-relevant="text">
            <template v-if="allConnections.length > 0">
                <ConnectionsCard
                    v-for="conn in allConnections"
                    :key="conn.id"
                    :connection-id="conn.id"
                    :connection="conn"
                    :name="conn.persona?.name"
                    :job-title="conn.connection.job_title"
                    :notes="getNotesSection(conn)"
                    :notes-pending="!!conn.notes_pending"
                    :on-details-click="handleDetails"
                    :is-dismissed="filter === FILTER.DISMISSED"
                    :has-public-profile="!!conn.public_profile"
                    :is-hris="!!conn.is_hris"
                    :is-inactive="!!conn.is_inactive"
                    @delete="onCardDelete"
                    @merge="onCardMerge" />
            </template>
            <template v-else>
                <CollaborationProfilePreview v-if="showEmptyState && public_profile_enabled" class="col-span-full pt-[13px] pb-6" />
                <p v-else class="col-span-full body-small text-ink-muted text-center" role="alert">No people match your current filter or search.</p>
            </template>
        </div>
        <div v-if="hasMore && !showEmptyState" class="flex justify-center pb-10">
            <BaseButton theme="neutral" :disabled="isLoadingMore" @click="loadMore">
                {{ isLoadingMore ? "Loading..." : "Load more" }}
            </BaseButton>
        </div>
    </BaseLayout>
    <CalendarIntegrationSettings v-if="calendar_import_enabled && !has_calendar_integration" v-model:open="showCalendarSettings" @close="showCalendarSettings = false" />
    <CsvImportDialog
        v-if="csv_import_enabled"
        v-model:open="showCsvImportDialog"
        :csv-template-url="csv_template_url"
        :csv-preview-url="csv_preview_url"
        :csv-import-url="csv_import_url"
        @imported="handleCsvImported" />
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useDebounceFn } from "@vueuse/core";
import CollabFilledIcon from "~img/collab_icons/collab-filled.svg";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import CalendarIntegrationSettings from "~vue/components/CalendarIntegration/CalendarIntegrationSettings.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import { useConnectionActions } from "~vue/composables/useConnectionActions";
import { useConnectionFacetFilters } from "~vue/composables/useConnectionFacetFilters";
import AddPeopleMenu from "~vue/Connections/AddPeopleMenu.vue";
import CollaborationProfilePreview from "~vue/Connections/CollaborationProfilePreview.vue";
import ConnectionsCard from "~vue/Connections/ConnectionsCard.vue";
import CsvImportDialog from "~vue/Connections/CsvImportDialog.vue";
import FacetFilterModal from "~vue/Connections/FacetFilterModal.vue";
import MergeConnectionsDialog from "~vue/Connections/MergeConnectionsDialog.vue";
import { getNotesSection } from "~vue/Connections/utils";
import type { ConnectionDict, FacetDefinition } from "~vue/types/connection";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted, provide, ref, watch } from "vue";

const FILTER = {
    ALL: "all",
    TEAM: "team",
    ORGANIZATION: "organization",
    DISMISSED: "dismissed",
};

const filterToNameMap = [
    { value: FILTER.ALL, name: "All" },
    { value: FILTER.TEAM, name: "My Team" },
    { value: FILTER.DISMISSED, name: "Dismissed" },
];

const sortFieldOptions = [
    { key: "last_discussed_desc", label: "Recently Discussed" },
    { key: "last_discussed_asc", label: "Oldest Discussed" },
    { key: "times_discussed_desc", label: "Most Discussed" },
    { key: "times_discussed_asc", label: "Least Discussed" },
    { key: "name_asc", label: "Name A-Z" },
    { key: "name_desc", label: "Name Z-A" },
];

type ConnectionsProps = {
    connections: Array<ConnectionDict>;
    new_connection_url: string;
    calendar_import_enabled?: boolean;
    has_calendar_integration?: boolean;
    from_calendar_url?: string;
    imported_count?: number | null;
    calendar_integration?: object;
    csv_import_enabled?: boolean;
    public_profile_enabled?: boolean;
    csv_template_url?: string;
    csv_preview_url?: string;
    csv_import_url?: string;
    facets?: Array<FacetDefinition>;
    has_more_connections?: boolean;
    next_offset?: number | null;
};

const {
    connections,
    new_connection_url,
    calendar_import_enabled = false,
    has_calendar_integration = false,
    from_calendar_url = "",
    imported_count = null,
    calendar_integration = {},
    csv_import_enabled = false,
    public_profile_enabled = false,
    csv_template_url = "",
    csv_preview_url = "",
    csv_import_url = "",
    facets = [],
    has_more_connections = false,
    next_offset = null,
} = defineProps<ConnectionsProps>();

provide("calendarIntegration", calendar_integration);

const { viewDetails } = useConnectionActions();

const filter = ref(FILTER.ALL);
const searchQuery = ref("");
const sortOption = ref("last_discussed_desc");
const showImportBanner = ref(imported_count !== null && imported_count > 0);
const showCalendarSettings = ref(false);
const showCsvImportDialog = ref(false);
const showCsvImportBanner = ref(false);
const csvImportCount = ref(0);
const isLoadingConnections = ref(false);

// Delete flow (kebab menu): "Remove from your people" opens a confirmation, then DELETEs it.
const showDeleteDialog = ref(false);
const connectionPendingDelete = ref<ConnectionDict | null>(null);
const deleteUrl = computed(() => `/api/connections/${connectionPendingDelete.value?.id ?? ""}/`);
const { execute: executeDelete, onFetchResponse: onDeleteResponse, onFetchError: onDeleteError } = useFetch(deleteUrl, { immediate: false }).delete();

const onCardDelete = (id: string) => {
    connectionPendingDelete.value = allConnections.value.find((c) => c.id === id) ?? null;
    if (connectionPendingDelete.value) {
        showDeleteDialog.value = true;
    }
};
const confirmDelete = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_DELETE, { source: "connections_kebab_menu" });
    executeDelete();
};
onDeleteResponse(() => {
    showDeleteDialog.value = false;
    connectionPendingDelete.value = null;
    filterConnections();
});
onDeleteError(() => {
    showDeleteDialog.value = false;
    connectionPendingDelete.value = null;
});

// Merge flow (kebab menu): "Merge" on a card opens the merge dialog anchored to that person.
const showKebabMergeDialog = ref(false);
const mergeAnchorConnection = ref<ConnectionDict | null>(null);
const onCardMerge = (id: string) => {
    mergeAnchorConnection.value = allConnections.value.find((c) => c.id === id) ?? null;
    if (mergeAnchorConnection.value) {
        showKebabMergeDialog.value = true;
    }
};

// Pagination state
const allConnections = ref([...connections]);
const currentOffset = ref(next_offset);
const hasMore = ref(has_more_connections);

const debouncedSearchQuery = useDebounceFn(() => {
    filterConnections();
}, 500);

watch(searchQuery, () => {
    isLoadingConnections.value = true;
    debouncedSearchQuery();
});

const queryParams = computed(() => {
    const lastUnderscore = sortOption.value.lastIndexOf("_");
    const sortField = sortOption.value.slice(0, lastUnderscore);
    const sortDir = sortOption.value.slice(lastUnderscore + 1);
    const params: { filter: string; sort_field: string; sort_dir: string; search?: string; facet_filters?: string } = {
        filter: filter.value,
        sort_field: sortField,
        sort_dir: sortDir,
    };
    if (searchQuery.value.trim()) {
        params.search = searchQuery.value.trim();
    }
    if (Object.keys(normalizedFacetFilters.value).length > 0) {
        params.facet_filters = JSON.stringify(normalizedFacetFilters.value);
    }
    return params;
});

const filterConnections = () => {
    router.get("/people/", queryParams.value, {
        preserveState: true,
        only: ["connections", "has_more_connections", "next_offset"],
        onFinish: () => {
            isLoadingConnections.value = false;
            // Reset pagination state from new SSR props
            allConnections.value = [...connections];
            hasMore.value = has_more_connections;
            currentOffset.value = next_offset;
        },
        onError: (error) => {
            logError("Failed to change filter:", error);
        },
    });
};

const {
    activeFacetFilters,
    activeFacetFilterEntries,
    clearFacetFilters,
    handleFacetFiltersChange,
    handleSingleFacetChange,
    hasActiveFacetFilters,
    meaningfulFacets,
    normalizeActiveFacetFilters,
    normalizedFacetFilters,
    removeFacetFilter,
    showSingleFacetControl,
    singleFacet,
    singleFacetOptions,
    singleFacetValue,
} = useConnectionFacetFilters(
    computed(() => facets),
    filterConnections,
);

// "Load more" pagination via coach_api
const loadMoreUrl = computed(() => {
    const params = new URLSearchParams(queryParams.value);
    params.set("offset", String(currentOffset.value));
    params.set("page_size", "24");
    return `/v1/connections/?${params.toString()}`;
});

const { data: moreData, isFetching: isLoadingMore, error: moreError, execute: fetchMoreConnections } = useFetch(loadMoreUrl, { immediate: false }).json();

async function loadMore() {
    if (currentOffset.value === null || !hasMore.value || isLoadingMore.value) {
        return;
    }
    await fetchMoreConnections();
    if (moreError.value) {
        logError("Failed to load more connections:", moreError.value);
        return;
    }
    if (moreData.value) {
        allConnections.value = [...allConnections.value, ...moreData.value.connections];
        hasMore.value = moreData.value.has_more;
        currentOffset.value = moreData.value.next_offset;
    }
}

const handleChangeFilter = (value: string) => {
    isLoadingConnections.value = true;
    filter.value = value;
    filterConnections();
};

const handleDetails = (id: string) => {
    if (filter.value === FILTER.DISMISSED) {
        router.visit(`/people/edit/${id}/`);
    } else {
        viewDetails(id);
    }
};

const createConnection = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_ADD_BUTTON_CLICKED);
    router.visit(new_connection_url);
};

const handleHelpLinkClick = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTIONS_HELP_LINK_CLICKED);
};

const navigateToCalendarImport = () => {
    if (!has_calendar_integration) {
        return;
    }
    logUserInteraction(AmplitudeEventNames.CALENDAR_IMPORT_BUTTON_CLICKED, { has_calendar_integration });
    router.visit(from_calendar_url);
};

const handleCsvImported = (result: { created?: number; updated?: number }) => {
    csvImportCount.value = (result?.created || 0) + (result?.updated || 0);
    showCsvImportBanner.value = csvImportCount.value > 0;
    filterConnections();
};

const dismissCsvBanner = () => {
    showCsvImportBanner.value = false;
};

const dismissBanner = () => {
    showImportBanner.value = false;
    // Update URL to remove import params
    const url = new URL(window.location.href);
    url.searchParams.delete("imported");
    url.searchParams.delete("linked");
    window.history.replaceState({}, "", url);
};

const showEmptyState = computed(
    () => !isLoadingConnections.value && allConnections.value.length === 0 && filter.value === FILTER.ALL && !searchQuery.value.trim() && !hasActiveFacetFilters.value,
);

onMounted(() => {
    const searchParams = new URLSearchParams(globalThis.location.search);
    const filterParam = searchParams.get("filter");
    const searchParam = searchParams.get("search");
    const sortFieldParam = searchParams.get("sort_field") ?? "last_discussed";
    const sortDirParam = searchParams.get("sort_dir") ?? "desc";

    filter.value = filterParam ?? FILTER.ALL;
    searchQuery.value = searchParam ?? "";
    sortOption.value = `${sortFieldParam}_${sortDirParam}`;

    const facetFiltersParam = searchParams.get("facet_filters");
    if (facetFiltersParam) {
        try {
            activeFacetFilters.value = normalizeActiveFacetFilters(JSON.parse(facetFiltersParam));
        } catch {
            /* ignore invalid JSON */
        }
    }
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";
.connections-grid {
    @apply grid gap-4 pb-10;
    grid-template-columns: repeat(auto-fill, minmax(327px, 1fr));
}
</style>
